import React, { useState, useMemo, useEffect } from "react";
import { X, Users, Cake } from "lucide-react";
import { colorOf } from "@/lib/constants";
import { fmtTime } from "@/lib/time";
import { api } from "@/lib/api";

// "Who's free?" dialog — answers the "is everyone around Saturday
// at 2?" question without leaving the dashboard. Picks a date and a
// time slot, then bins every human family member into Free / Busy
// based on whether their tagged events overlap the chosen window.
//
// Fetches events for the chosen day directly from /events?from_iso=…
// so multi-day trips that started weeks ago AND dates 2+ months out
// both resolve correctly (the dashboard's `upcoming` array is
// limited and would silently miss either case).
//
// Props:
//   members   array of family members (pets ignored — they're always "free")
//   fallbackEvents  events the dashboard already has (used while the
//                   per-date fetch is in flight, and as a fallback if
//                   the fetch fails for any reason)
//   onClose   close the dialog
export default function WhosFreeDialog({ members = [], fallbackEvents = [], onClose }) {
  const today = new Date();
  const defaultDate = today.toISOString().slice(0, 10);
  const [dateStr, setDateStr] = useState(defaultDate);
  const [slot, setSlot] = useState("all_day"); // morning / midday / evening / all_day
  const [dayEvents, setDayEvents] = useState(null); // null = not loaded yet, [] = loaded empty

  // Time windows for each slot, in local clock (minutes-from-midnight).
  const SLOTS = {
    morning: { label: "Morning", startMin: 8 * 60, endMin: 12 * 60 },
    midday: { label: "Midday", startMin: 12 * 60, endMin: 17 * 60 },
    evening: { label: "Evening", startMin: 17 * 60, endMin: 22 * 60 },
    all_day: { label: "All day", startMin: 0, endMin: 24 * 60 },
  };

  // Re-fetch every time the date changes. The query window is the
  // full local day so multi-day trips overlapping the date show up.
  useEffect(() => {
    let cancelled = false;
    const dayStart = new Date(`${dateStr}T00:00:00`);
    const dayEnd = new Date(`${dateStr}T23:59:59`);
    api
      .listEvents({
        from_iso: dayStart.toISOString(),
        to_iso: dayEnd.toISOString(),
      })
      .then((evs) => {
        if (!cancelled) setDayEvents(Array.isArray(evs) ? evs : []);
      })
      .catch(() => {
        if (!cancelled) setDayEvents(null); // fall back to dashboard list
      });
    return () => {
      cancelled = true;
    };
  }, [dateStr]);

  const events = dayEvents != null ? dayEvents : fallbackEvents;

  // Birthday hits on the chosen date — match by month/day so a member
  // born on 17 May 1985 lights up on every 17th of May regardless of
  // year. Pets included (a kid asking "is grandma free for Jasper's
  // birthday?" deserves the answer). Returned as a flat array so the
  // header chip can list multiple ("Both London and Mum") cleanly.
  const birthdaysOnDay = useMemo(() => {
    if (!dateStr || !members) return [];
    const [y, m, d] = dateStr.split("-").map((n) => parseInt(n, 10));
    if (!m || !d) return [];
    const out = [];
    for (const mem of members) {
      const bd = (mem?.birthday || "").trim();
      if (!bd) continue;
      // Accept "YYYY-MM-DD" and bare "MM-DD". Anything else: skip silently.
      const parts = bd.split("-");
      let bm, bday;
      if (parts.length === 3) {
        bm = parseInt(parts[1], 10);
        bday = parseInt(parts[2], 10);
      } else if (parts.length === 2) {
        bm = parseInt(parts[0], 10);
        bday = parseInt(parts[1], 10);
      } else {
        continue;
      }
      if (bm === m && bday === d) {
        // Compute age if the year is known. Lemon turning 4? Worth saying.
        let age = null;
        if (parts.length === 3) {
          const by = parseInt(parts[0], 10);
          if (by && by > 1900) age = y - by;
        }
        out.push({ member: mem, age });
      }
    }
    return out;
  }, [dateStr, members]);

  const { free, busy } = useMemo(() => {
    const slotCfg = SLOTS[slot] || SLOTS.all_day;
    const windowStart = new Date(`${dateStr}T00:00:00`);
    windowStart.setMinutes(windowStart.getMinutes() + slotCfg.startMin);
    const windowEnd = new Date(`${dateStr}T00:00:00`);
    windowEnd.setMinutes(windowEnd.getMinutes() + slotCfg.endMin);
    // Preload a map of member.id → array of conflicting events.
    const byMember = new Map();
    for (const m of members) {
      if (m.role === "pet") continue;
      byMember.set(m.id, []);
    }
    for (const ev of events || []) {
      if (ev.source === "birthday") continue; // birthdays aren't "busy"
      const start = ev.starts_at ? new Date(ev.starts_at) : null;
      // Fall back to the end being start + 1h when ends_at is missing —
      // untimed events still occupy their slot visually.
      const end = ev.ends_at
        ? new Date(ev.ends_at)
        : start
          ? new Date(start.getTime() + 60 * 60 * 1000)
          : null;
      if (!start || !end) continue;
      if (end <= windowStart || start >= windowEnd) continue;
      for (const mid of ev.member_ids || []) {
        if (byMember.has(mid)) byMember.get(mid).push(ev);
      }
    }
    const free = [];
    const busy = [];
    for (const m of members) {
      if (m.role === "pet") continue;
      const conflicts = byMember.get(m.id) || [];
      if (conflicts.length === 0) free.push({ member: m });
      else busy.push({ member: m, events: conflicts });
    }
    return { free, busy };
  }, [members, events, dateStr, slot]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/45 backdrop-blur-[2px] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="whos-free-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[color:var(--surface)] w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl border border-[color:var(--line)] shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-[color:var(--surface)] border-b border-[color:var(--line-soft)] px-5 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" strokeWidth={1.8} />
            <span className="font-display text-lg tracking-tight">Who's free?</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ff-btn ff-btn-icon"
            aria-label="Close"
            data-testid="whos-free-close"
          >
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className="label-tiny block mb-1.5">Day</label>
              <input
                type="date"
                value={dateStr}
                onChange={(e) => setDateStr(e.target.value)}
                className="ff-input tabular"
                data-testid="whos-free-date"
              />
            </div>
            <div>
              <label className="label-tiny block mb-1.5">When</label>
              <select
                value={slot}
                onChange={(e) => setSlot(e.target.value)}
                className="ff-input"
                data-testid="whos-free-slot"
              >
                {Object.entries(SLOTS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Birthday banner — when the chosen date is a member's
              birthday we surface a calm pink chip just under the date
              row. "Mum's birthday · turns 42" reads in one breath, so
              the planner sees it before they go scheduling something
              else into that day. Multiple-birthday days (rare but
              possible — twins, partner & dog) join with " · ". */}
          {birthdaysOnDay.length > 0 && (
            <div
              className="rounded-[10px] px-3 py-2 inline-flex items-start gap-2"
              style={{ background: "#FFD2DD" }}
              data-testid="whos-free-birthday-chip"
            >
              <Cake className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.8} />
              <div className="text-[13px] leading-snug">
                {birthdaysOnDay.map((b, i) => (
                  <React.Fragment key={b.member.id}>
                    {i > 0 && <span className="text-[color:var(--ink-3)]"> · </span>}
                    <strong className="font-semibold">{b.member.name}'s birthday</strong>
                    {b.age != null && b.age > 0 && (
                      <span className="text-[color:var(--ink-2)]"> · turns {b.age}</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}          {/* Free column */}
          <section>
            <div className="label-tiny text-[color:var(--ink-3)] mb-2">
              Free · {free.length}
            </div>
            {free.length === 0 ? (
              <div className="label-tiny text-[color:var(--ink-3)] italic">
                No one's free in this window.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {free.map(({ member }) => (
                  <span
                    key={member.id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[color:var(--line)] bg-[color:var(--bg)]/70"
                    data-testid={`whos-free-free-${member.id}`}
                  >
                    {member.avatar_image ? (
                      <img
                        src={member.avatar_image}
                        alt=""
                        className="w-5 h-5 rounded-full object-cover"
                      />
                    ) : (
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-[11px]"
                        style={{ background: colorOf(member.color).hex }}
                      >
                        {member.avatar_emoji}
                      </span>
                    )}
                    <span className="text-sm">{member.name}</span>
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* Busy column — shows the conflict so it's obvious why. */}
          <section>
            <div className="label-tiny text-[color:var(--ink-3)] mb-2">
              Busy · {busy.length}
            </div>
            {busy.length === 0 ? (
              <div className="label-tiny text-[color:var(--ink-3)] italic">
                Nobody has anything tagged.
              </div>
            ) : (
              <ul className="space-y-2">
                {busy.map(({ member, events }) => {
                  const first = events[0];
                  const fStart = first?.starts_at ? new Date(first.starts_at) : null;
                  const fEnd = first?.ends_at ? new Date(first.ends_at) : null;
                  const timeLabel = fStart
                    ? fEnd
                      ? `${fmtTime(fStart)}–${fmtTime(fEnd)}`
                      : fmtTime(fStart)
                    : "";
                  return (
                    <li
                      key={member.id}
                      className="flex items-center gap-2.5"
                      data-testid={`whos-free-busy-${member.id}`}
                    >
                      {member.avatar_image ? (
                        <img
                          src={member.avatar_image}
                          alt=""
                          className="w-7 h-7 rounded-full object-cover flex-shrink-0 opacity-70"
                        />
                      ) : (
                        <span
                          className="w-7 h-7 rounded-full flex items-center justify-center text-sm flex-shrink-0 opacity-70"
                          style={{ background: colorOf(member.color).hex }}
                        >
                          {member.avatar_emoji}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm truncate">
                          <strong className="font-semibold">{member.name}</strong>
                          <span className="text-[color:var(--ink-3)]">
                            {" "}· {first.title}
                          </span>
                        </div>
                        {timeLabel && (
                          <div className="label-tiny text-[color:var(--ink-3)]">
                            {timeLabel}
                            {events.length > 1 && ` · +${events.length - 1} more`}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
